/**
 * Changes against an earlier period, for KPI tiles (StatTile, TrendIndicator's `change`). Display
 * goes through the formatters; these only work the numbers out.
 */

/**
 * The fractional change from `before` to `now` (0.12 is +12%); null when there's nothing to compare
 * with: no earlier value, or one at or below zero, where a percentage means nothing.
 */
export function changeFrom(now: number | null | undefined, before: number | null | undefined): number | null {
  if (now == null || before == null || !Number.isFinite(now) || !Number.isFinite(before)) return null;
  return before > 0 ? (now - before) / before : null;
}

/**
 * The change between two fractions in percentage points (0.46 against 0.41 is 5 points); null when
 * either is missing.
 */
export function pointsFrom(now: number | null | undefined, before: number | null | undefined): number | null {
  if (now == null || before == null || !Number.isFinite(now) || !Number.isFinite(before)) return null;
  return (now - before) * 100;
}

/** How a change is written: a fraction shown as a whole percent, or points to one decimal. */
export type ChangeKind = 'percent' | 'points';

/**
 * True when a change shows as zero once written ("0%", or "0.0 pp" for points), so it gets no arrow
 * and no good or bad colour. Rounds the magnitude half up, as the formatters round half away from zero.
 */
export function changeRoundsToZero(change: number, kind: ChangeKind = 'percent'): boolean {
  const abs = Math.abs(change);
  return kind === 'points' ? Math.round(abs * 10) === 0 : Math.round(abs * 100) === 0;
}
